import AnalyticsProvider, {
  IDimensions,
  IEvent,
  IKeyValue,
  INamespaceDefinition,
} from './analytics-provider';

type PAQEntry = (string | number | undefined)[];

declare global {
  interface Window {
    Matomo: any;
    _paq: PAQEntry[];
  }
}

function getPaq() {
  /* eslint-disable no-underscore-dangle */
  if (!window._paq) {
    window._paq = [];
  }
  return window._paq;
  /* eslint-enable no-underscore-dangle */
}

// Matomo Documentation at https://developer.matomo.org/guides/tracking-javascript-guide

export default class MatomoAnalyticsProvider extends AnalyticsProvider {
  siteId: number;
  trackerUrl: string;
  nextDimensionsSlot: number;
  dimensionToSlot: IKeyValue;

  constructor(
    siteId: number,
    trackerUrl: string,
    {
      namespaceType,
      namespace,
    }: INamespaceDefinition = {} as INamespaceDefinition
  ) {
    super(namespaceType, namespace);
    if (!siteId || typeof siteId !== 'number') {
      throw new Error('siteId must be set');
    }
    if (!trackerUrl || typeof trackerUrl !== 'string') {
      throw new Error('trackerUrl must be set');
    }
    this.siteId = siteId;
    this.trackerUrl = trackerUrl;
    this.nextDimensionsSlot = 1;
    this.dimensionToSlot = {};
  }

  initialize() {
    const paq = getPaq();

    // If Matomo hasn't been initialized yet, first set it up
    if ('object' !== typeof window.Matomo) {
      paq.push(['enableLinkTracking']);
      paq.push(['setRequestMethod', 'GET']);

      paq.push(['setTrackerUrl', this.trackerUrl]);
      paq.push(['setSiteId', this.siteId]);

      require('./matomo.min.js');
    } else {
      // If Matomo already has a base siteId and trackerUrl, add new tracker
      window.Matomo.addTracker(this.trackerUrl, this.siteId);
      // Ensure the new tracker respects the GET request method
      paq.push(['setRequestMethod', 'GET']);
    }

    return window.Matomo.initialized;
  }

  event({ category, action, label: name, value, namespace }: IEvent) {
    if (this.shouldTrack(namespace)) {
      const paq = getPaq();
      paq.push(['trackEvent', category, action, name, value]);
    }
  }

  setDimension(dimensions: IDimensions) {
    const paq = getPaq();
    for (let key in dimensions) {
      const value = dimensions[key];
      const slot = this.dimensionToSlot[key] || this.nextDimensionsSlot++;
      this.dimensionToSlot[key] = slot;
      paq.push(['setCustomDimension', slot, value]);
    }
  }

  // @ts-ignore
  option(key: string, value: any) {
    // TODO
  }

  pageView(route: string, namespace: string) {
    if (this.shouldTrack(namespace)) {
      // Documentation at https://developer.matomo.org/guides/spa-tracking#measuring-single-page-apps-complete-example
      const paq = getPaq();
      // track which page brought the user here
      paq.push(['setReferrerUrl', route]);
      paq.push(['setCustomUrl', route]);
      paq.push(['deleteCustomVariables', 'page']);
      paq.push(['trackPageView']);
      paq.push(['enableLinkTracking']);
    }
  }

  error({ filename, lineno, colno, error }: ErrorEvent) {
    const category = 'JavaScript Errors';
    const action = `${error.message} at ${filename}:${lineno}:${colno}`;
    const label = error.stack;
    this.event({ category, action, label });
  }
}
